/**
 * Report Builder Page
 * 
 * A wizard-style interface for creating customizable ESG reports.
 * Includes template selection, section configuration, AI narrative generation,
 * and data completeness validation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Palette,
  CheckSquare,
  Sparkles,
  Eye,
  Download,
  Loader2,
  Check,
  AlertTriangle,
  Info,
  ChevronRight,
  Building2,
  ShieldCheck,
  BarChart3,
  Target,
  Leaf,
  Users,
  BookOpen,
  Star,
  Clock,
  Zap,
  RefreshCw,
  Edit3,
  X,
  Upload,
  Wand2,
  Scale,
  Shield,
  TrendingUp
} from 'lucide-react';
import useAINarrative, { contextBuilders } from '@/hooks/useAINarrative';
import AICoPilot from '@/components/AICoPilot';

// Wizard steps
const WIZARD_STEPS = [
  { id: 1, name: 'Template', icon: FileText, description: 'Choose report type' },
  { id: 2, name: 'Sections', icon: CheckSquare, description: 'Select content' },
  { id: 3, name: 'Content', icon: Sparkles, description: 'Generate & edit' },
  { id: 4, name: 'Branding', icon: Palette, description: 'Customize look' },
  { id: 5, name: 'Review', icon: Eye, description: 'Preview & generate' },
];

// Category icons
const CATEGORY_ICONS = {
  front: BookOpen,
  governance: Building2,
  strategy: Target,
  risk: ShieldCheck,
  metrics: BarChart3,
  social: Users,
  appendix: FileText,
};

// Category labels
const CATEGORY_LABELS = {
  front: 'Front Matter',
  governance: 'Governance',
  strategy: 'Strategy',
  risk: 'Risk Management',
  metrics: 'Metrics & Targets',
  social: 'Social',
  appendix: 'Appendices',
};

/**
 * ASRS Assurance Requirements by Group and Year
 * Based on Australian Sustainability Reporting Standards phased implementation
 */
const ASSURANCE_REQUIREMENTS = {
  // Group 1 - Large entities
  1: {
    limitedStart: 2025,      // FY 2024-25
    reasonableStart: 2030,   // FY 2029-30
    name: 'Group 1'
  },
  // Group 2 - Mid-tier entities
  2: {
    limitedStart: 2027,      // FY 2026-27
    reasonableStart: 2032,   // FY 2031-32
    name: 'Group 2'
  },
  // Group 3 - Smaller entities
  3: {
    limitedStart: 2028,      // FY 2027-28
    reasonableStart: 2033,   // FY 2032-33
    name: 'Group 3'
  }
};

/**
 * Get assurance level required for a given ASRS group and financial year
 * @param {number} asrsGroup - ASRS Reporting Group (1, 2, or 3)
 * @param {number} financialYear - Financial year (e.g., 2026 for FY 2025-26)
 * @returns {Object} { level: 'none'|'limited'|'reasonable', label: string, color: string }
 */
const getAssuranceLevel = (asrsGroup, financialYear) => {
  if (!asrsGroup || !ASSURANCE_REQUIREMENTS[asrsGroup]) {
    return { level: 'none', label: 'Not specified', color: 'slate' };
  }

  const req = ASSURANCE_REQUIREMENTS[asrsGroup];

  if (financialYear >= req.reasonableStart) {
    return {
      level: 'reasonable',
      label: 'Reasonable Assurance Required',
      color: 'emerald',
      description: `${req.name} entities must obtain reasonable assurance from FY ${req.reasonableStart - 1}-${String(req.reasonableStart).slice(-2)}`
    };
  } else if (financialYear >= req.limitedStart) {
    return {
      level: 'limited',
      label: 'Limited Assurance Required',
      color: 'amber',
      description: `${req.name} entities must obtain limited assurance from FY ${req.limitedStart - 1}-${String(req.limitedStart).slice(-2)}`
    };
  } else {
    return {
      level: 'voluntary',
      label: 'Voluntary Disclosure',
      color: 'blue',
      description: `${req.name} mandatory reporting begins FY ${req.limitedStart - 1}-${String(req.limitedStart).slice(-2)}`
    };
  }
};

// Assurance Badge Component
const AssuranceBadge = ({ asrsGroup, financialYear }) => {
  const assurance = getAssuranceLevel(asrsGroup, financialYear);

  if (assurance.level === 'none') return null;

  const badgeColors = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  const iconColors = {
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    slate: 'text-slate-500'
  };

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${badgeColors[assurance.color]}`}
      title={assurance.description}>
      <Shield className={`w-3.5 h-3.5 ${iconColors[assurance.color]}`} />
      <span>{assurance.label}</span>
    </div>
  );
};

export default function ReportBuilderPage() {
  const navigate = useNavigate();
  const { generateNarrative, isGenerating } = useAINarrative();

  // Core state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // User/org data
  const [currentOrg, setCurrentOrg] = useState(null);
  const [userId, setUserId] = useState(null);

  // Report builder data
  const [templates, setTemplates] = useState([]);
  const [sections, setSections] = useState([]);
  const [themes, setThemes] = useState([]);
  const [sampleWording, setSampleWording] = useState([]);

  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showCustomTemplateBuilder, setShowCustomTemplateBuilder] = useState(false);
  const [customTemplateName, setCustomTemplateName] = useState('');
  const [customTemplateDescription, setCustomTemplateDescription] = useState('');
  const [savingCustomTemplate, setSavingCustomTemplate] = useState(false);

  // Draft state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSections, setSelectedSections] = useState({});
  const [sectionContent, setSectionContent] = useState({});
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [reportName, setReportName] = useState('');
  const [platformLogo, setPlatformLogo] = useState(null);
  const [financialYear, setFinancialYear] = useState(new Date().getFullYear());
  const [reportingPeriod, setReportingPeriod] = useState('financial_year'); // 'financial_year', 'all_time', 'custom'
  const [directorsName, setDirectorsName] = useState('');
  const [directorsTitle, setDirectorsTitle] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [directorsSignature, setDirectorsSignature] = useState(null); // Base64 signature image
  const [authorisedOfficerSource, setAuthorisedOfficerSource] = useState('governance'); // 'governance' or 'manual'
  const [governancePersonnel, setGovernancePersonnel] = useState([]);
  const [selectedOfficerId, setSelectedOfficerId] = useState('');

  // Step 5 confirmation checkbox
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // Data completeness
  const [dataCompleteness, setDataCompleteness] = useState({});
  const [orgData, setOrgData] = useState({});

  // Editing state
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');

  // Saved draft state
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showDraftSelector, setShowDraftSelector] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Output format state (PDF or Word)
  const [outputFormat, setOutputFormat] = useState('pdf'); // 'pdf' or 'docx'

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Get current user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/login');
        return;
      }

      // Get user's internal ID
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) {
        navigate('/login');
        return;
      }
      setUserId(userData.id);

      // Get organisation - use internal user ID, not Supabase auth ID
      // First try as owner
      let { data: orgs } = await supabase
        .from('organisations')
        .select('*')
        .eq('owner_id', userData.id)
        .order('is_primary', { ascending: false })
        .limit(1);

      // If not found as owner, check organisation_members
      if (!orgs?.length) {
        const { data: memberOrgs } = await supabase
          .from('organisation_members')
          .select('organisation_id, organisations(*)')
          .eq('user_id', userData.id)
          .limit(1);

        if (memberOrgs?.length > 0 && memberOrgs[0].organisations) {
          orgs = [memberOrgs[0].organisations];
        }
      }

      if (orgs?.length > 0) {
        setCurrentOrg(orgs[0]);
        // Report name will be set when template is selected
        setReportName('');

        // Fetch org-specific data for AI context
        await fetchOrgData(orgs[0].id);

        // Fetch saved drafts for this organization
        await fetchSavedDrafts(orgs[0].id);
      } else {
        console.warn('No organisation found for user');
      }

      // Fetch templates, sections, themes
      const [templatesRes, sectionsRes, themesRes, wordingRes, platformLogoRes] = await Promise.all([
        supabase.from('report_templates').select('*').eq('is_active', true).order('complexity_level'),
        supabase.from('report_sections').select('*').eq('is_active', true).order('default_order'),
        supabase.from('report_themes').select('*').eq('is_active', true),
        supabase.from('sample_wording').select('*').eq('is_active', true),
        supabase.from('system_settings').select('value').eq('key', 'platform_logo').maybeSingle(),
      ]);

      if (templatesRes.data) setTemplates(templatesRes.data);
      if (sectionsRes.data) setSections(sectionsRes.data);
      if (themesRes.data) {
        setThemes(themesRes.data);
        // Set default theme
        const defaultTheme = themesRes.data.find(t => t.name === 'Lucent Sky Default');
        if (defaultTheme) setSelectedTheme(defaultTheme);
      }
      if (wordingRes.data) setSampleWording(wordingRes.data);

      if (platformLogoRes.data?.value) {
        const parsed = typeof platformLogoRes.data.value === 'string' ? JSON.parse(platformLogoRes.data.value) : platformLogoRes.data.value;
        if (parsed?.logo_url) {
          setPlatformLogo(parsed.logo_url);
        }
      }

      // Fetch custom templates for this organization
      if (orgs?.length > 0) {
        await fetchCustomTemplates(orgs[0].id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report builder');
      setLoading(false);
    }
  };

  // Fetch custom templates for the organization
  const fetchCustomTemplates = async (orgId) => {
    try {
      const { data, error } = await supabase
        .from('custom_templates')
        .select('*')
        .eq('organisation_id', orgId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }

      setCustomTemplates(data || []);
    } catch (err) {
    }
  };

  // Save current section selection as a custom template
  const saveAsCustomTemplate = async () => {
    if (!currentOrg || !customTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const selectedSlugList = Object.entries(selectedSections)
      .filter(([_, v]) => v.included)
      .map(([slug, config], index) => ({
        slug,
        order: index + 1,
        required: config.required || false,
      }));

    if (selectedSlugList.length === 0) {
      toast.error('Please select at least one section');
      return;
    }

    setSavingCustomTemplate(true);
    try {
      const { data, error } = await supabase
        .from('custom_templates')
        .insert({
          organisation_id: currentOrg.id,
          created_by: userId,
          name: customTemplateName.trim(),
          description: customTemplateDescription.trim() || null,
          section_config: selectedSlugList,
          base_template_id: selectedTemplate?.id || null,
          default_theme_id: selectedTheme?.id || null,
        })
        .select()
        .single();

      if (error) throw error;

      setCustomTemplates(prev => [data, ...prev]);
      setShowCustomTemplateBuilder(false);
      setCustomTemplateName('');
      setCustomTemplateDescription('');
      toast.success('Custom template saved!');
    } catch (error) {
      console.error('Error saving custom template:', error);
      console.error('Error details:', error.message, error.details, error.hint);
      toast.error(`Failed to save custom template: ${error.message || 'Unknown error'}`);
    } finally {
      setSavingCustomTemplate(false);
    }
  };

  // Select a custom template
  const handleCustomTemplateSelect = (customTemplate) => {
    // Create a fake template object that matches the structure
    const fakeTemplate = {
      id: customTemplate.id,
      slug: `custom-${customTemplate.id}`,
      name: customTemplate.name,
      description: customTemplate.description,
      primary_standard: 'Custom',
      complexity_level: 'custom',
      sections: customTemplate.section_config || [],
      isCustom: true,
    };

    setSelectedTemplate(fakeTemplate);

    // Pre-select sections based on custom template config
    const templateSections = {};
    (customTemplate.section_config || []).forEach(s => {
      templateSections[s.slug] = {
        included: true,
        required: s.required || false,
      };
    });
    setSelectedSections(templateSections);

    // Set default theme if specified
    if (customTemplate.default_theme_id) {
      const theme = themes.find(t => t.id === customTemplate.default_theme_id);
      if (theme) setSelectedTheme(theme);
    }

    // Update report name
    if (currentOrg) {
      setReportName(`${currentOrg.name} - ${customTemplate.name}`);
    }
  };

  // Delete a custom template
  const deleteCustomTemplate = async (templateId) => {
    try {
      const { error } = await supabase
        .from('custom_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      setCustomTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting custom template:', error);
      toast.error('Failed to delete template');
    }
  };

  const fetchOrgData = async (orgId) => {
    try {
      // Fetch ALL emissions data (for historical comparisons)
      const { data: emissions, error: emissionsError } = await supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', orgId)
        .order('entry_date', { ascending: true });

      if (emissionsError) console.error('Emissions fetch error:', emissionsError);
      // Fetch climate risks (may not exist)
      let risks = [];
      try {
        const { data } = await supabase
          .from('climate_risks')
          .select('*')
          .eq('organisation_id', orgId);
        risks = data || [];
      } catch (e) {
      }

      // Fetch governance data (from 'governance' table, not 'governance_structure')
      let governance = [];
      try {
        const { data, error } = await supabase
          .from('governance')
          .select('*')
          .eq('organisation_id', orgId);
        if (!error) governance = data || [];
      } catch (e) {
      }

      // Fetch carbon offsets
      const { data: offsets } = await supabase
        .from('carbon_offsets')
        .select('*')
        .eq('organisation_id', orgId);

      // Fetch offset allocations (carbon credits applied)
      const { data: allocations } = await supabase
        .from('offset_allocations')
        .select('*')
        .eq('organisation_id', orgId);

      // Fetch social metrics
      let social = [];
      try {
        const { data, error } = await supabase
          .from('social_metrics')
          .select('*')
          .eq('organisation_id', orgId);
        if (!error) social = data || [];
      } catch (e) {
      }

      // Fetch scenario analysis
      let scenarios = [];
      try {
        const { data, error } = await supabase
          .from('scenario_analysis')
          .select('*')
          .eq('organisation_id', orgId);
        if (!error) scenarios = data || [];
      } catch (e) {
      }

      // Fetch materiality assessments
      let materiality = [];
      try {
        const { data, error } = await supabase
          .from('materiality_assessments')
          .select('*')
          .eq('organisation_id', orgId);
        if (!error) materiality = data || [];
      } catch (e) {
      }

      // Fetch organisation reduction targets
      const { data: orgDetails } = await supabase
        .from('organisations')
        .select('reduction_target_percent, baseline_year, target_year, baseline_emissions')
        .eq('id', orgId)
        .single();

      // Calculate data completeness - check for actual data
      const hasOffsets = (offsets?.length || 0) > 0 || (allocations?.length || 0) > 0;

      const completeness = {
        emissions_data: { met: (emissions?.length || 0) > 0, count: emissions?.length || 0 },
        climate_risks: { met: risks.length > 0, count: risks.length },
        governance_structure: { met: governance.length > 0, count: governance.length },
        governance: { met: governance.length > 0, count: governance.length },
        carbon_offsets: { met: hasOffsets, count: (offsets?.length || 0) + (allocations?.length || 0) },
        offset_allocations: { met: (allocations?.length || 0) > 0, count: allocations?.length || 0 },
        social_metrics: { met: social.length > 0, count: social.length },
        scenario_analysis: { met: scenarios.length > 0, count: scenarios.length },
        materiality_assessment: { met: materiality.length > 0, count: materiality.length },
        reduction_targets: { met: !!orgDetails?.reduction_target_percent, count: orgDetails?.reduction_target_percent ? 1 : 0 },
        organisation_profile: { met: true, count: 1 },
      };
      setDataCompleteness(completeness);

      // Group emissions by financial year for historical comparisons
      const emissionsByFY = {};
      (emissions || []).forEach(e => {
        // Use entry_date or start_date
        const dateStr = e.start_date || e.entry_date;
        if (!dateStr) return;
        const date = new Date(dateStr);
        // Australian FY: July 1 - June 30
        const fy = date.getMonth() >= 6 ? date.getFullYear() + 1 : date.getFullYear();
        if (!emissionsByFY[fy]) emissionsByFY[fy] = [];
        emissionsByFY[fy].push(e);
      });

      // Fetch governance personnel for officer dropdown
      let personnelList = [];
      try {
        const { data: personnelData } = await supabase
          .from('governance_personnel')
          .select('*')
          .eq('organisation_id', orgId)
          .eq('is_active', true)
          .order('role_type', { ascending: true });
        personnelList = personnelData || [];
      } catch (e) {
      }

      // Also get responsible officer from governance table
      if (governance.length > 0 && governance[0].responsible_officer_name) {
        const respOfficer = {
          id: 'responsible_officer',
          full_name: governance[0].responsible_officer_name,
          title: governance[0].responsible_officer_title,
          role_type: 'responsible_officer',
          is_authorised_signatory: true
        };
        personnelList.unshift(respOfficer);
      }

      setGovernancePersonnel(personnelList);

      // Store raw data for AI context
      setOrgData({
        emissions: emissions || [],
        emissionsByFY,
        risks: risks || [],
        governance: governance || [],
        offsets: offsets || [],
        allocations: allocations || [],
        social: social || [],
        scenarios: scenarios || [],
        materiality: materiality || [],
        reductionTargets: orgDetails || {},
      });

    } catch (error) {
      console.error('Error fetching org data:', error);
    }
  };

  // Fetch saved drafts for the organization
  const fetchSavedDrafts = async (orgId) => {
    try {
      const { data, error } = await supabase
        .from('report_drafts')
        .select('id, name, status, financial_year, updated_at, template_slug')
        .eq('organisation_id', orgId)
        .eq('status', 'draft')
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setSavedDrafts(data || []);
    } catch (err) {
      console.error('Error fetching saved drafts:', err);
    }
  };

  // Save current progress as a draft
  const saveDraft = async (showToast = true) => {
    if (!currentOrg || !selectedTemplate) {
      if (showToast) toast.error('Please select a template first');
      return null;
    }

    setSaving(true);
    try {
      const draftData = {
        organisation_id: currentOrg.id,
        created_by: userId,
        template_id: selectedTemplate.id,
        template_slug: selectedTemplate.slug,
        name: reportName || `${selectedTemplate.name} - FY${financialYear}`,
        financial_year: financialYear,
        theme_id: selectedTheme?.id || null,
        section_content: {
          ...sectionContent,
          _wizard_state: {
            currentStep,
            selectedSections,
            reportingPeriod,
            directorsName,
            directorsTitle,
            reportDate,
            directorsSignature,
            authorisedOfficerSource,
            selectedOfficerId,
          }
        },
        directors_name: directorsName || null,
        directors_title: directorsTitle || null,
        status: 'draft',
        overall_completeness: calculateCompleteness(),
        updated_at: new Date().toISOString(),
      };

      let result;
      if (currentDraftId) {
        // Update existing draft
        const { data, error } = await supabase
          .from('report_drafts')
          .update(draftData)
          .eq('id', currentDraftId)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Create new draft
        const { data, error } = await supabase
          .from('report_drafts')
          .insert(draftData)
          .select()
          .single();

        if (error) throw error;
        result = data;
        setCurrentDraftId(result.id);
      }

      setLastSavedAt(new Date());
      if (showToast) toast.success('Progress saved');

      // Refresh saved drafts list
      await fetchSavedDrafts(currentOrg.id);

      return result;
    } catch (error) {
      console.error('Error saving draft:', error);
      if (showToast) toast.error('Failed to save progress');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Load a saved draft
  const loadDraft = async (draftId) => {
    try {
      setLoading(true);
      const { data: draft, error } = await supabase
        .from('report_drafts')
        .select('*')
        .eq('id', draftId)
        .single();

      if (error) throw error;
      if (!draft) throw new Error('Draft not found');

      // Find the template
      const template = templates.find(t => t.id === draft.template_id || t.slug === draft.template_slug);
      if (template) {
        setSelectedTemplate(template);
      }

      // Restore state from draft
      setCurrentDraftId(draft.id);
      setReportName(draft.name || '');
      setFinancialYear(draft.financial_year || new Date().getFullYear());
      setDirectorsName(draft.directors_name || '');
      setDirectorsTitle(draft.directors_title || '');

      // Find theme
      if (draft.theme_id) {
        const theme = themes.find(t => t.id === draft.theme_id);
        if (theme) setSelectedTheme(theme);
      }

      // Restore section content and wizard state
      const content = draft.section_content || {};
      const wizardState = content._wizard_state || {};

      // Remove wizard state from section content before setting
      const { _wizard_state, ...cleanSectionContent } = content;
      setSectionContent(cleanSectionContent);

      // Restore wizard state
      if (wizardState.currentStep) setCurrentStep(wizardState.currentStep);
      if (wizardState.selectedSections) setSelectedSections(wizardState.selectedSections);
      if (wizardState.reportingPeriod) setReportingPeriod(wizardState.reportingPeriod);
      if (wizardState.reportDate) setReportDate(wizardState.reportDate);
      if (wizardState.directorsSignature) setDirectorsSignature(wizardState.directorsSignature);
      if (wizardState.authorisedOfficerSource) setAuthorisedOfficerSource(wizardState.authorisedOfficerSource);
      if (wizardState.selectedOfficerId) setSelectedOfficerId(wizardState.selectedOfficerId);

      setLastSavedAt(new Date(draft.updated_at));
      setShowDraftSelector(false);
      toast.success('Draft loaded successfully');
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Failed to load draft');
    } finally {
      setLoading(false);
    }
  };

  // Delete a saved draft
  const deleteDraft = async (draftId) => {
    try {
      const { error } = await supabase
        .from('report_drafts')
        .delete()
        .eq('id', draftId);

      if (error) throw error;

      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
      }

      await fetchSavedDrafts(currentOrg.id);
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  // Start a new report (clear current draft)
  const startNewReport = () => {
    setCurrentDraftId(null);
    setSelectedTemplate(null);
    setSelectedSections({});
    setSectionContent({});
    setSelectedTheme(null);
    setReportName('');
    setFinancialYear(new Date().getFullYear());
    setReportingPeriod('financial_year');
    setDirectorsName('');
    setDirectorsTitle('');
    setReportDate(new Date().toISOString().split('T')[0]);
    setDirectorsSignature(null);
    setAuthorisedOfficerSource('governance');
    setSelectedOfficerId('');
    setCurrentStep(1);
    setLastSavedAt(null);
    setShowDraftSelector(false);
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);

    // Update report name to reflect the selected template
    if (currentOrg) {
      setReportName(`${currentOrg.name} - ${template.name}`);
    } else {
      setReportName(template.name);
    }

    // Pre-select sections based on template
    const templateSections = {};
    if (template.sections) {
      template.sections.forEach(s => {
        templateSections[s.slug] = {
          included: true,
          required: s.required,
        };
      });
    }
    setSelectedSections(templateSections);
  };

  // Toggle section selection
  const toggleSection = (sectionSlug, required) => {
    if (required) return; // Can't deselect required sections

    setSelectedSections(prev => ({
      ...prev,
      [sectionSlug]: {
        ...prev[sectionSlug],
        included: !prev[sectionSlug]?.included,
      },
    }));
  };

  // Generate AI content for a section
  // Helper function to calculate emissions by scope for a given financial year
  const calculateEmissionsByScope = (emissions, fy = null) => {
    let filtered = emissions || [];
    if (fy && orgData.emissionsByFY) {
      filtered = orgData.emissionsByFY[fy] || [];
    }

    // Use total_tco2e or co2_equivalent (both are used in the codebase)
    const getEmissionValue = (e) => e.total_tco2e || e.co2_equivalent || 0;

    const scope1 = filtered.filter(e =>
      ['fuel_petrol', 'fuel_diesel', 'fuel_lpg', 'natural_gas'].includes(e.category)
    ).reduce((sum, e) => sum + getEmissionValue(e), 0);

    const scope2 = filtered.filter(e =>
      ['electricity', 'electricity_greenpower'].includes(e.category)
    ).reduce((sum, e) => sum + getEmissionValue(e), 0);

    // Note: Scope 3 tracking has been removed from this platform
    return { scope1, scope2, total: scope1 + scope2 };
  };

  const handleGenerateContent = async (section) => {
    if (!currentOrg) {
      toast.error('No organisation selected');
      return;
    }
    try {
      // Calculate current and previous year emissions for comparisons
      const currentFY = financialYear;
      const previousFY = financialYear - 1;

      const currentYearEmissions = calculateEmissionsByScope(null, currentFY);
      const previousYearEmissions = calculateEmissionsByScope(null, previousFY);

      // Check what historical data is actually available
      const availableYears = Object.keys(orgData.emissionsByFY || {}).sort();
      const hasPreviousYearData = availableYears.includes(String(previousFY));
      const hasCurrentYearData = availableYears.includes(String(currentFY));

      // Build context based on section type
      let contextData = {
        // Always include these metadata fields
        organisation_name: currentOrg.name,
        organisation_website: currentOrg.website || null,
        organisation_abn: currentOrg.abn || null,
        organisation_industry: currentOrg.industry || null,
        reporting_financial_year: `FY${currentFY - 1}/${String(currentFY).slice(-2)}`,
        data_availability: {
          has_current_year_data: hasCurrentYearData,
          has_previous_year_data: hasPreviousYearData,
          available_financial_years: availableYears,
          note: !hasPreviousYearData
            ? 'IMPORTANT: No previous year data available. Do NOT make year-over-year comparisons or assumptions about trends.'
            : 'Previous year data is available for comparison.',
        },
        placeholder_handling: currentOrg.website
          ? `If you need to reference the organisation website, use: ${currentOrg.website}`
          : 'IMPORTANT: No organisation website is configured. Do NOT include any placeholder text like "[insert website URL]" or "[website]". Simply omit any sentences that would require a website URL.',
      };

      if (section.category === 'governance') {
        contextData = {
          ...contextData,
          governance_data: orgData.governance,
          has_governance_data: (orgData.governance?.length || 0) > 0,
          board_info: 'Board of Directors',
          note_for_ai: orgData.governance?.length > 0
            ? 'Use the provided governance data.'
            : 'IMPORTANT: No governance structure data available. Write generic guidance about what should be disclosed, do not make up specific details.',
        };
      } else if (section.category === 'metrics') {
        contextData = {
          ...contextData,
          current_year_emissions: hasCurrentYearData ? currentYearEmissions : null,
          previous_year_emissions: hasPreviousYearData ? previousYearEmissions : null,
          year_over_year_change: (hasPreviousYearData && hasCurrentYearData && previousYearEmissions.total > 0)
            ? ((currentYearEmissions.total - previousYearEmissions.total) / previousYearEmissions.total * 100).toFixed(1) + '%'
            : null,
          methodology: 'GHG Protocol Corporate Standard with Australian NGA Factors',
          emission_categories: [...new Set(orgData.emissions?.map(e => e.category) || [])],
          total_emission_entries: orgData.emissions?.length || 0,
          reduction_targets: orgData.reductionTargets,
          has_reduction_targets: !!orgData.reductionTargets?.reduction_target_percent,
          note_for_ai: !hasPreviousYearData
            ? 'IMPORTANT: Previous year data not available. Report only current year figures. Do NOT assume or fabricate year-over-year changes.'
            : 'Previous year data available - you may include year-over-year comparisons.',
        };
      } else if (section.category === 'risk') {
        const physicalRisks = orgData.risks.filter(r => r.risk_type === 'physical');
        const transitionRisks = orgData.risks.filter(r => r.risk_type === 'transition');
        const opportunities = orgData.risks.filter(r => r.risk_type === 'opportunity');

        contextData = {
          ...contextData,
          physical_risks: physicalRisks,
          transition_risks: transitionRisks,
          opportunities: opportunities,
          has_risk_data: orgData.risks?.length > 0,
          note_for_ai: orgData.risks?.length > 0
            ? 'Use the provided climate risk data.'
            : 'IMPORTANT: No climate risk assessment data available. Write about the importance of climate risk assessment and what should be disclosed, but do not fabricate specific risks.',
        };
      } else if (section.slug === 'front-executive-summary') {
        contextData = {
          ...contextData,
          total_emissions: hasCurrentYearData ? currentYearEmissions.total : null,
          previous_year_total: hasPreviousYearData ? previousYearEmissions.total : null,
          carbon_offsets_purchased: orgData.offsets?.length || 0,
          carbon_credits_applied: orgData.allocations?.length || 0,
          reduction_targets: orgData.reductionTargets,
          has_reduction_targets: !!orgData.reductionTargets?.reduction_target_percent,
          key_achievements: [
            orgData.emissions?.length > 0 ? 'Comprehensive emissions tracking implemented' : null,
            orgData.risks?.length > 0 ? 'Climate risk assessment completed' : null,
            orgData.offsets?.length > 0 ? 'Carbon offset strategy established' : null,
          ].filter(Boolean),
          note_for_ai: 'Generate a professional executive summary. Only include achievements and statistics that are backed by actual data. If no previous year data exists, focus on establishing the baseline rather than trends.',
        };
      } else if (section.slug === 'metrics-carbon-offsets') {
        contextData = {
          ...contextData,
          carbon_offsets: orgData.offsets || [],
          offset_allocations: orgData.allocations || [],
          total_offsets: orgData.offsets?.length || 0,
          total_allocations: orgData.allocations?.length || 0,
          has_offset_data: (orgData.offsets?.length || 0) > 0 || (orgData.allocations?.length || 0) > 0,
          note_for_ai: (orgData.offsets?.length || 0) > 0 || (orgData.allocations?.length || 0) > 0
            ? 'Use the provided carbon offset data.'
            : 'IMPORTANT: No carbon offset data available. Write about the role of carbon offsets in emissions management without fabricating specific purchases.',
        };
      } else if (section.slug === 'metrics-reduction-targets') {
        contextData = {
          ...contextData,
          baseline_year: orgData.reductionTargets?.baseline_year,
          baseline_emissions: orgData.reductionTargets?.baseline_emissions,
          target_year: orgData.reductionTargets?.target_year,
          reduction_target_percent: orgData.reductionTargets?.reduction_target_percent,
          current_emissions: hasCurrentYearData ? currentYearEmissions.total : null,
          has_target_data: !!orgData.reductionTargets?.reduction_target_percent,
          note_for_ai: orgData.reductionTargets?.reduction_target_percent
            ? 'Use the provided reduction target data.'
            : 'IMPORTANT: No formal reduction targets have been set. Write about the importance of setting science-based targets without fabricating specific commitments.',
        };
      } else if (section.category === 'social') {
        const socialData = orgData.social || [];
        contextData = {
          ...contextData,
          social_metrics: socialData,
          has_social_data: socialData.length > 0,
          note_for_ai: socialData.length > 0
            ? 'Use the provided social metrics data.'
            : 'IMPORTANT: No social metrics data available. Write about the importance of social sustainability metrics without fabricating specific figures.',
        };
      } else if (section.slug.includes('materiality')) {
        const materialityData = orgData.materiality || [];
        contextData = {
          ...contextData,
          materiality_topics: materialityData,
          has_materiality_data: materialityData.length > 0,
          note_for_ai: materialityData.length > 0
            ? 'Use the provided materiality assessment data.'
            : 'IMPORTANT: No materiality assessment data available. Write about the importance of materiality assessment without fabricating specific topics.',
        };
      } else if (section.slug.includes('scenario')) {
        const scenarioData = orgData.scenarios || [];
        contextData = {
          ...contextData,
          scenario_analyses: scenarioData,
          has_scenario_data: scenarioData.length > 0,
          note_for_ai: scenarioData.length > 0
            ? 'Use the provided scenario analysis data.'
            : 'IMPORTANT: No scenario analysis data available. Write about the importance of climate scenario analysis without fabricating specific results.',
        };
      } else {
        contextData = {
          ...contextData,
          note_for_ai: 'Generate professional content for this section. Only include specific data points if they are provided. Clearly distinguish between factual statements and general guidance.',
        };
      }
      const result = await generateNarrative({
        organisationId: currentOrg.id,
        sectionSlug: section.slug,
        contextData,
      });
      if (result.success && result.content) {
        setSectionContent(prev => ({
          ...prev,
          [section.slug]: {
            content: result.content,
            isAiGenerated: true,
            aiAuditId: result.auditId,
            generatedAt: new Date().toISOString(),
            wasEdited: false,
          },
        }));
        toast.success(`Generated content for ${section.display_name}`);
      } else {
        console.error('AI generation failed:', result.error);
        toast.error(result.error || 'Failed to generate content');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(`Failed to generate content: ${error.message}`);
    }
  };

  // Track which section is currently generating
  const [generatingSection, setGeneratingSection] = useState(null);

  // Wrapper for AI generation with loading state
  const handleGenerateWithLoading = async (section) => {
    setGeneratingSection(section.slug);
    await handleGenerateContent(section);
    setGeneratingSection(null);
  };

  // Save edited content
  const handleSaveEdit = (sectionSlug) => {
    setSectionContent(prev => ({
      ...prev,
      [sectionSlug]: {
        ...prev[sectionSlug],
        content: editContent,
        wasEdited: true,
        editedAt: new Date().toISOString(),
      },
    }));
    setEditingSection(null);
    setEditContent('');
    toast.success('Content saved');
  };

  // Use sample wording
  const handleUseSampleWording = (section, wording) => {
    setSectionContent(prev => {
      const updated = {
        ...prev,
        [section.slug]: {
          content: wording.content,
          isAiGenerated: false,
          source: wording.source,
          wasEdited: false,
          isSampleText: true,
          sampleWordingId: String(wording.id),
          sampleWordingTitle: wording.title,
        },
      };
      return updated;
    });
    toast.success('Sample wording applied - Use "Refine with AI" to personalize it with your data');
  };

  // Refine sample text with AI - replaces placeholders with org data
  const [refiningSection, setRefiningSection] = useState(null);

  // Helper to get period label based on reporting period type
  const getPeriodLabel = () => {
    if (reportingPeriod === 'financial_year') {
      return `FY${financialYear - 1}/${String(financialYear).slice(-2)}`;
    } else if (reportingPeriod === 'all_time') {
      return 'All-Time (to date)';
    } else if (reportingPeriod === 'custom') {
      return 'Custom Period';
    }
    return `FY${financialYear - 1}/${String(financialYear).slice(-2)}`;
  };

  // Helper to calculate scope breakdown from emissions data (Scope 1 & 2 only)
  const calculateScopeBreakdown = (emissions) => {
    const scope1Categories = ['fuel_petrol', 'fuel_diesel', 'fuel_lpg', 'natural_gas', 'stationary_fuel'];
    const scope2Categories = ['electricity', 'electricity_greenpower'];

    const scope1 = emissions
      .filter(e => scope1Categories.includes(e.category))
      .reduce((sum, e) => sum + (e.total_tco2e || e.co2_equivalent || 0), 0);

    const scope2 = emissions
      .filter(e => scope2Categories.includes(e.category))
      .reduce((sum, e) => sum + (e.total_tco2e || e.co2_equivalent || 0), 0);

    const total = scope1 + scope2;

    return { scope1, scope2, total };
  };

  const handleRefineSampleText = async (section) => {
    const content = sectionContent[section.slug];
    if (!content?.content) {
      toast.error('No content to refine');
      return;
    }

    setRefiningSection(section.slug);
    try {
      // Get period-specific label
      const periodLabel = getPeriodLabel();
      const isAllTime = reportingPeriod === 'all_time';
      const isCustomPeriod = reportingPeriod === 'custom';

      // Get relevant emissions for the period
      let relevantEmissions = [];
      if (isAllTime) {
        relevantEmissions = orgData.emissions || [];
      } else if (reportingPeriod === 'financial_year') {
        relevantEmissions = orgData.emissionsByFY?.[financialYear] || [];
      } else {
        relevantEmissions = orgData.emissions || [];
      }

      // Calculate scope breakdown
      const scopes = calculateScopeBreakdown(relevantEmissions);

      // Calculate percentages (Scope 1 & 2 only)
      const scope1Pct = scopes.total > 0 ? ((scopes.scope1 / scopes.total) * 100).toFixed(1) : 0;
      const scope2Pct = scopes.total > 0 ? ((scopes.scope2 / scopes.total) * 100).toFixed(1) : 0;

      // Build emissions label with scope breakdown
      const emissionsLabel = isAllTime
        ? `${scopes.total.toFixed(2)} tCO₂-e (all-time total)`
        : isCustomPeriod
          ? `${scopes.total.toFixed(2)} tCO₂-e (custom period)`
          : `${scopes.total.toFixed(2)} tCO₂-e`;

      // Build context with all available org data including granular scope breakdown
      const contextData = {
        organisation_name: currentOrg.name,
        financial_year: financialYear,
        period_label: periodLabel,
        period_type: reportingPeriod,
        is_all_time: isAllTime,
        is_custom_period: isCustomPeriod,
        report_date: reportDate,
        abn: currentOrg.abn || '',
        industry: currentOrg.industry || '',
        // Period-aware emissions with GRANULAR SCOPE BREAKDOWN (Scope 1 & 2 only)
        total_emissions: scopes.total,
        scope1_emissions: scopes.scope1,
        scope2_emissions: scopes.scope2,
        scope1_percentage: scope1Pct,
        scope2_percentage: scope2Pct,
        emissions_label: emissionsLabel,
        emissions_count: orgData.emissions?.length || 0,
        // Organisation details
        organisation_website: currentOrg.website || null,
        // Governance data
        governance_items: orgData.governance || [],
        personnel: orgData.governance?.filter(g => g.item_type === 'board_member' || g.item_type === 'officer') || [],
        // Risk data
        climate_risks: orgData.risks || [],
        risk_count: orgData.risks?.length || 0,
        high_risks: orgData.risks?.filter(r => r.impact === 'high' || r.impact === 'severe')?.length || 0,
        // Social data  
        social_metrics: orgData.social || [],
        // Offsets data
        total_offsets: orgData.offsets?.reduce((sum, o) => sum + (o.amount_tco2e || 0), 0) || 0,
        offsets_count: orgData.offsets?.length || 0,
        // Materiality data
        materiality_topics: orgData.materiality || [],
        materiality_count: orgData.materiality?.length || 0,
        // Scenarios data
        scenario_analyses: orgData.scenarios || [],
        // Reduction targets
        reduction_target: orgData.reductionTargets?.reduction_target_percent || null,
        target_year: orgData.reductionTargets?.target_year || null,
        baseline_year: orgData.reductionTargets?.baseline_year || null,
      };

      // Build website instruction
      const websiteInstruction = currentOrg.website
        ? `- Website: ${currentOrg.website} (use this exact URL if website is referenced)`
        : '- Website: NOT CONFIGURED - Remove any lines that reference a website URL. Do NOT include placeholder text like "[insert website URL]" or "[website]".';

      const result = await generateNarrative({
        organisationId: currentOrg.id,
        sectionSlug: section.slug,
        contextData: {
          ...contextData,
          original_sample_text: content.content,
          mode: 'refine_sample',
          note_for_ai: `You are refining sample text for a sustainability report. The original text contains placeholders like [Organisation Name], [Year], [X], etc. 

LANGUAGE: Always use Australian English spelling (e.g., "organisation" not "organization", "recognised" not "recognized", "colour" not "color", "behaviour" not "behavior"). Use Australian terminology like "financial year" not "fiscal year".

REPORTING PERIOD: ${periodLabel} (${reportingPeriod === 'all_time' ? 'All-Time cumulative data' : reportingPeriod === 'custom' ? 'Custom date range' : 'Single financial year'})

CRITICAL INSTRUCTIONS:
1. Replace ALL placeholders with actual data from the context provided
2. Replace [Organisation Name] or similar with "${currentOrg.name}"
3. Replace [Year], [FY], or year references with "${periodLabel}"
4. Replace emissions placeholders with the EXACT scope-level data provided below
5. ${isAllTime ? 'Note this is ALL-TIME data, not a single year - adjust wording accordingly (e.g., "since records began" or "cumulative emissions to date")' : ''}
6. ${isCustomPeriod ? 'Note this is a CUSTOM PERIOD - adjust wording to reference the custom reporting period' : ''}
7. Replace any percentage placeholders with actual data where available
8. Keep the same structure and professional tone
9. Do NOT add assumptions or fabricate data that isn't provided
10. If a placeholder has no corresponding data, replace with "[Data not yet recorded]" or similar professional note
11. WEBSITE HANDLING: ${currentOrg.website ? `Replace any website placeholders with: ${currentOrg.website}` : 'REMOVE any sentences that mention a website URL. Do NOT leave "[insert website URL]" or similar placeholders.'}
12. IMPORTANT: This report covers Scope 1 and Scope 2 emissions ONLY. Do NOT reference or mention Scope 3 emissions.

EMISSIONS DATA (USE THESE EXACT VALUES - SCOPE 1 & 2 ONLY):
- Total Emissions: ${scopes.total.toFixed(2)} tCO₂-e
- Scope 1 (Direct): ${scopes.scope1.toFixed(2)} tCO₂-e (${scope1Pct}% of total) - includes fuel combustion, company vehicles
- Scope 2 (Indirect - Energy): ${scopes.scope2.toFixed(2)} tCO₂-e (${scope2Pct}% of total) - purchased electricity

OTHER AVAILABLE DATA:
- Organisation: ${currentOrg.name}${currentOrg.abn ? ` (ABN: ${currentOrg.abn})` : ''}
- Industry: ${currentOrg.industry || 'Not specified'}
${websiteInstruction}
- Climate Risks Identified: ${contextData.risk_count} (${contextData.high_risks} high/severe)
- Carbon Offsets: ${contextData.total_offsets.toFixed(2)} tCO₂-e
- Materiality Topics: ${contextData.materiality_count} assessed
${contextData.reduction_target ? `- Reduction Target: ${contextData.reduction_target}% by ${contextData.target_year}` : '- Reduction Target: Not yet set'}

Original sample text to refine:
${content.content}

Return the refined text with all placeholders replaced using actual organisation data. Use the EXACT emission figures provided above. Maintain professional language suitable for a formal sustainability report. Do NOT include any Scope 3 references.`,
        },
      });

      if (result.success && result.content) {
        setSectionContent(prev => ({
          ...prev,
          [section.slug]: {
            content: result.content,
            isAiGenerated: true,
            wasRefined: true,
            refinedFrom: content.sampleWordingTitle || 'Sample text',
            aiAuditId: result.auditId,
            generatedAt: new Date().toISOString(),
            wasEdited: false,
          },
        }));
        toast.success(`Refined "${section.display_name}" with your organisation data`);
      } else {
        toast.error(result.error || 'Failed to refine content');
      }
    } catch (error) {
      console.error('Error refining content:', error);
      toast.error(`Failed to refine content: ${error.message}`);
    } finally {
      setRefiningSection(null);
    }
  };

  // Generate full report
  const handleGenerateReport = async () => {
    if (!currentOrg || !selectedTemplate) return;

    setGenerating(true);
    try {
      // First verify we have a valid userId
      if (!userId) {
        throw new Error('User ID not found. Please refresh the page and try again.');
      }

      // Get current session for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }
      // Create report draft (stores all the wizard configuration)
      const draftPayload = {
        organisation_id: currentOrg.id,
        created_by: userId,
        template_id: selectedTemplate.id,
        template_slug: selectedTemplate.slug,
        name: reportName || `${selectedTemplate.name} - FY${financialYear}`,
        financial_year: financialYear,
        theme_id: selectedTheme?.id || null,
        section_content: sectionContent,
        directors_name: directorsName || null,
        directors_title: directorsTitle || null,
        report_date: reportDate || null,
        directors_signature: directorsSignature || null,
        status: 'generating',
        overall_completeness: calculateCompleteness(),
      };
      const { data: draft, error: draftError } = await supabase
        .from('report_drafts')
        .insert(draftPayload)
        .select()
        .single();

      if (draftError) {
        console.error('Draft creation error:', draftError);
        const errorMsg = draftError.message || '';
        const errorCode = draftError.code || '';

        if (errorCode === '42501' || errorCode === 'PGRST301' ||
          errorMsg.includes('permission denied') ||
          errorMsg.includes('violates row-level security') ||
          errorMsg.includes('403')) {
          throw new Error('Permission denied. Please run migration 011_fix_all_remaining_rls.sql in Supabase SQL Editor.');
        }

        throw new Error(draftError.message || 'Failed to create report draft');
      }
      // Trigger PDF generation via the Edge Function (same flow as existing reports)
      // This creates the report_jobs entry AND triggers the Render PDF service
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      // Determine the actual report name to use
      const finalReportName = reportName || `${currentOrg.name} - ${selectedTemplate.name}`;
      const response = await fetch(`${supabaseUrl}/functions/v1/request-pdf`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          user_id: session.user.id, // Supabase auth ID for the Edge Function
          organisation_id: currentOrg.id,
          financial_year: financialYear,
          report_type: selectedTemplate.slug,
          user_email: session.user.email,
          user_name: session.user.user_metadata?.full_name || directorsName,
          organisation_name: currentOrg.name,
          is_sample: false, // Full report from Report Builder
          // Additional Report Builder context
          draft_id: draft.id,
          template_name: selectedTemplate.name,
          report_name: finalReportName, // Pass the actual report name
          output_format: outputFormat, // 'pdf' or 'docx'
        })
      });

      // Handle response - check status first, then parse body
      if (!response.ok) {
        // Try to get error details
        let errorMessage = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // If we can't parse JSON, use status-based message
        }

        // Handle 409 conflict (job already exists)
        if (response.status === 409) {
          toast.warning('A report is already being generated. Please wait for it to complete.');
          navigate('/reports');
          return;
        }

        throw new Error(errorMessage);
      }

      // Success - parse response
      let result = {};
      try {
        result = await response.json();
      } catch (parseError) {
        console.warn('Could not parse success response:', parseError);
        // Even if we can't parse, the job was likely created
      }
      toast.success('Report generation started! You\'ll receive an email when it\'s ready (typically 1-2 minutes).');
      navigate('/reports');

    } catch (error) {
      console.error('Error generating report:', error);
      const message = error.message || 'Failed to generate report. Please try again.';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  // Calculate completeness percentage
  const calculateCompleteness = () => {
    const selectedSectionsList = Object.entries(selectedSections)
      .filter(([_, v]) => v.included)
      .map(([slug]) => slug);

    const withContent = selectedSectionsList.filter(slug =>
      sectionContent[slug]?.content?.length > 0
    ).length;

    return selectedSectionsList.length > 0
      ? Math.round((withContent / selectedSectionsList.length) * 100)
      : 0;
  };

  // Generate HTML preview (client-side) with theme support
  // Generate HTML preview (client-side) with theme support
  const generatePreview = () => {
    const selectedSectionsList = Object.entries(selectedSections)
      .filter(([_, v]) => v.included)
      .map(([slug]) => slug);

    const orderedSections = sections
      .filter(s => selectedSectionsList.includes(s.slug))
      .sort((a, b) => (a.default_order || 0) - (b.default_order || 0));

    // Get theme colors (fallback to defaults)
    const theme = {
      primary: selectedTheme?.primary_color || '#003B5C',
      secondary: selectedTheme?.secondary_color || '#74A57F',
      accent: selectedTheme?.accent_color || '#E9C46A',
      text: selectedTheme?.text_color || '#1A1A2E',
      background: selectedTheme?.background_color || '#FFFFFF',
      headingFont: selectedTheme?.heading_font || 'Outfit',
      bodyFont: selectedTheme?.body_font || 'Inter',
    };

    // Calculate emissions data for charts
    const fyEmissions = orgData.emissionsByFY?.[financialYear] || [];
    const totalEmissions = fyEmissions.reduce((sum, e) => sum + (e.total_tco2e || e.co2_equivalent || 0), 0);

    // Calculate scope breakdown (Scope 1 & 2 only)
    const scope1 = fyEmissions.filter(e => ['fuel_petrol', 'fuel_diesel', 'fuel_lpg', 'natural_gas'].includes(e.category))
      .reduce((sum, e) => sum + (e.total_tco2e || e.co2_equivalent || 0), 0);
    const scope2 = fyEmissions.filter(e => ['electricity', 'electricity_greenpower'].includes(e.category))
      .reduce((sum, e) => sum + (e.total_tco2e || e.co2_equivalent || 0), 0);

    // Calculate category breakdown
    const byCategory = {};
    fyEmissions.forEach(e => {
      const cat = (e.category || 'other').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      byCategory[cat] = (byCategory[cat] || 0) + (e.total_tco2e || e.co2_equivalent || 0);
    });

    // Generate SVG pie chart for scopes (Scope 1 & 2 only)
    const generateScopePieChart = () => {
      if (totalEmissions === 0) return '';
      const data = [
        { label: 'Scope 1', value: scope1, color: theme.primary },
        { label: 'Scope 2', value: scope2, color: theme.secondary },
      ].filter(d => d.value > 0);

      // Pie chart centered at (90, 90) with radius 65
      const cx = 90, cy = 90, r = 65;
      let startAngle = 0;
      const paths = data.map((d) => {
        const angle = (d.value / totalEmissions) * 360;
        const endAngle = startAngle + angle;
        const largeArc = angle > 180 ? 1 : 0;
        const startRad = (startAngle - 90) * Math.PI / 180;
        const endRad = (endAngle - 90) * Math.PI / 180;
        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy + r * Math.sin(startRad);
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy + r * Math.sin(endRad);
        startAngle = endAngle;
        return '<path d="M ' + cx + ',' + cy + ' L ' + x1 + ',' + y1 + ' A ' + r + ',' + r + ' 0 ' + largeArc + ',1 ' + x2 + ',' + y2 + ' Z" fill="' + d.color + '" />';
      }).join('');

      const legend = data.map((d, i) =>
        '<g transform="translate(190, ' + (40 + i * 28) + ')">' +
        '<rect width="16" height="16" fill="' + d.color + '" rx="3"/>' +
        '<text x="24" y="13" font-size="12" fill="' + theme.text + '">' + d.label + ': ' + d.value.toFixed(1) + ' tCO₂e (' + ((d.value / totalEmissions) * 100).toFixed(0) + '%)</text>' +
        '</g>'
      ).join('');

      return '<svg viewBox="0 0 450 180" style="max-width: 100%; height: auto; margin: 20px 0;">' +
        '<text x="90" y="18" text-anchor="middle" font-size="14" font-weight="600" fill="' + theme.primary + '">Emissions by Scope</text>' +
        '<g transform="translate(0, 5)">' + paths + '</g>' +
        legend +
        '</svg>';
    };

    // Generate bar chart for categories
    const generateCategoryChart = () => {
      const categories = Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 5);
      if (categories.length === 0) return '';
      const maxValue = Math.max(...categories.map(c => c[1]));

      const bars = categories.map((c, i) => {
        const width = (c[1] / maxValue) * 250;
        return '<g transform="translate(0, ' + (i * 35) + ')">' +
          '<text x="0" y="15" font-size="11" fill="' + theme.text + '">' + c[0].substring(0, 20) + '</text>' +
          '<rect x="120" y="2" width="' + width + '" height="18" fill="' + theme.secondary + '" rx="3"/>' +
          '<text x="' + (125 + width) + '" y="15" font-size="10" fill="' + theme.text + '">' + c[1].toFixed(1) + '</text>' +
          '</g>';
      }).join('');

      return '<svg viewBox="0 0 400 ' + (categories.length * 35 + 30) + '" style="max-width: 100%; height: auto; margin: 20px 0;">' +
        '<text x="0" y="15" font-size="14" font-weight="600" fill="' + theme.primary + '">Top Emission Categories (tCO₂e)</text>' +
        '<g transform="translate(0, 30)">' + bars + '</g>' +
        '</svg>';
    };

    // Simple markdown to HTML converter
    const markdownToHtml = (text) => {
      if (!text) return '';
      return text
        // Headers
        .replace(/^### (.*$)/gim, '<h4>$1</h4>')
        .replace(/^## (.*$)/gim, '<h3>$1</h3>')
        .replace(/^# (.*$)/gim, '<h2>$1</h2>')
        // Bold
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Bullet lists
        .replace(/^\s*[-•]\s+(.*)$/gim, '<li>$1</li>')
        // Numbered lists
        .replace(/^\s*\d+\.\s+(.*)$/gim, '<li>$1</li>')
        // Wrap consecutive li elements in ul
        .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
        // Line breaks (double newline = paragraph)
        .replace(/\n\n/g, '</p><p>')
        // Single line breaks
        .replace(/\n/g, '<br/>')
        // Wrap in paragraph if not already wrapped
        .replace(/^(?!<[hup])/gim, '<p>')
        .replace(/(?<![>])$/gim, '</p>');
    };

    // Build sections HTML
    const sectionsHtml = orderedSections.map((section, idx) => {
      const content = sectionContent[section.slug];
      const hasContent = content?.content?.length > 0;

      // Add emissions visualizations for metrics sections
      let metricsHtml = '';
      if (section.slug.includes('metrics-ghg') || section.slug.includes('emissions-scope')) {
        metricsHtml = '<div class="metrics-summary">' +
          '<div class="metric-card"><div class="value">' + totalEmissions.toFixed(1) + '</div><div class="label">Total tCO₂e</div></div>' +
          '<div class="metric-card"><div class="value">' + fyEmissions.length + '</div><div class="label">Data Entries</div></div>' +
          '<div class="metric-card"><div class="value">' + Object.keys(byCategory).length + '</div><div class="label">Categories</div></div>' +
          '</div>' +
          '<div class="chart-container">' + generateScopePieChart() + '</div>' +
          '<div class="chart-container">' + generateCategoryChart() + '</div>';
      }

      const aiBadge = content?.isAiGenerated ? '<span class="ai-badge">AI Assisted</span>' : '';
      // Convert markdown to HTML for the content
      const formattedContent = hasContent ? markdownToHtml(content.content) : '<div class="no-content">Content will be added before final report generation.</div>';

      return '<div class="section">' +
        '<h2>' + (idx + 1) + '. ' + section.display_name + aiBadge + '</h2>' +
        metricsHtml +
        '<div class="section-content">' + formattedContent + '</div>' +
        '</div>';
    }).join('');

    // Build declaration HTML
    let declarationHtml = '';
    if (directorsName) {
      const sigImg = directorsSignature ? '<img src="' + directorsSignature + '" alt="Signature" style="max-height: 50px;" />' : '';
      declarationHtml = '<div class="declaration">' +
        '<h2>Declaration</h2>' +
        '<p>This report has been prepared in accordance with applicable sustainability reporting standards.</p>' +
        '<div class="signature-block">' + sigImg +
        '<div class="signature-line">' +
        '<strong>' + directorsName + '</strong><br/>' +
        (directorsTitle || 'Authorised Officer') + '<br/>' +
        (currentOrg?.name || 'Organisation') + '<br/>' +
        new Date(reportDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) +
        '</div>' +
        '</div>' +
        '</div>';
    }

    // Build TOC
    const tocItems = orderedSections.map((s, i) =>
      '<li>' + (i + 1) + '. ' + s.display_name + '<span class="page-num">' + (i + 3) + '</span></li>'
    ).join('');

    const standardBadge = selectedTemplate?.primary_standard
      ? '<div class="standard">' + selectedTemplate.primary_standard + ' Compliant</div>'
      : '';

    // Build logo HTML if available
    const logoHtml = currentOrg?.logo_path
      ? '<img src="' + currentOrg.logo_path + '" alt="' + (currentOrg?.name || 'Organisation') + ' Logo" style="max-height: 80px; max-width: 200px; margin-bottom: 20px;" />'
      : '';

    // Build ABN HTML if available
    const abnHtml = currentOrg?.abn
      ? '<div class="abn">ABN: ' + currentOrg.abn + '</div>'
      : '';

    const platformLogoHtml = platformLogo
      ? '<div style="margin-top: 15px;"><img src="' + platformLogo + '" alt="Platform Logo" style="height: 24px; opacity: 0.6; mix-blend-mode: multiply;" /></div>'
      : '<p>Generated by Lucent Sky ESG Report Builder</p>';

    const html = '<!DOCTYPE html><html><head>' +
      '<link href="https://fonts.googleapis.com/css2?family=' + theme.headingFont + ':wght@400;600;700&family=' + theme.bodyFont + ':wght@400;500&display=swap" rel="stylesheet">' +
      '<style>' +
      '* { box-sizing: border-box; }' +
      'body { font-family: "' + theme.bodyFont + '", sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: ' + theme.text + '; background: ' + theme.background + '; line-height: 1.6; }' +
      'h1, h2, h3 { font-family: "' + theme.headingFont + '", sans-serif; }' +
      '.cover { text-align: center; padding: 80px 40px; background: linear-gradient(135deg, ' + theme.primary + ', ' + theme.primary + 'dd); color: white; margin: -40px -40px 40px; border-radius: 0 0 20px 20px; }' +
      '.cover h1 { font-size: 36px; margin: 0 0 15px; font-weight: 700; }' +
      '.cover .org { font-size: 20px; opacity: 0.95; }' +
      '.cover .abn { font-size: 14px; opacity: 0.8; margin-top: 8px; }' +
      '.cover .period { font-size: 14px; opacity: 0.8; margin-top: 25px; }' +
      '.cover .standard { display: inline-block; background: rgba(255,255,255,0.2); padding: 6px 16px; border-radius: 20px; font-size: 12px; margin-top: 20px; text-transform: uppercase; }' +
      '.toc { background: linear-gradient(135deg, ' + theme.primary + '08, ' + theme.secondary + '08); padding: 30px; border-radius: 12px; margin-bottom: 40px; border: 1px solid ' + theme.primary + '15; }' +
      '.toc h2 { font-size: 18px; color: ' + theme.primary + '; margin: 0 0 20px; padding-bottom: 10px; border-bottom: 2px solid ' + theme.secondary + '; }' +
      '.toc ul { list-style: none; padding: 0; margin: 0; }' +
      '.toc li { padding: 10px 0; border-bottom: 1px solid ' + theme.primary + '10; }' +
      '.toc .page-num { float: right; color: ' + theme.secondary + '; font-weight: 500; }' +
      '.section { margin-bottom: 40px; page-break-inside: avoid; }' +
      '.section h2 { color: ' + theme.primary + '; font-size: 22px; border-left: 4px solid ' + theme.secondary + '; padding-left: 16px; margin-bottom: 20px; }' +
      '.section h3 { color: ' + theme.primary + '; font-size: 18px; margin: 25px 0 15px; }' +
      '.section h4 { color: ' + theme.primary + '; font-size: 16px; margin: 20px 0 10px; }' +
      '.section-content { line-height: 1.8; }' +
      '.section-content p { margin: 0 0 15px; text-align: justify; }' +
      '.section-content ul { margin: 15px 0; padding-left: 25px; }' +
      '.section-content li { margin: 8px 0; }' +
      '.section-content strong { color: ' + theme.primary + '; }' +
      '.no-content { color: #94a3b8; font-style: italic; background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }' +
      '.metrics-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 25px 0; }' +
      '.metric-card { background: linear-gradient(135deg, ' + theme.primary + '05, ' + theme.secondary + '08); border: 1px solid ' + theme.primary + '15; border-radius: 10px; padding: 20px; text-align: center; }' +
      '.metric-card .value { font-size: 28px; font-weight: 700; color: ' + theme.primary + '; }' +
      '.metric-card .label { font-size: 12px; color: ' + theme.text + '99; text-transform: uppercase; margin-top: 5px; }' +
      '.chart-container { background: white; border: 1px solid ' + theme.primary + '10; border-radius: 12px; padding: 20px; margin: 20px 0; }' +
      '.ai-badge { background: linear-gradient(135deg, #8b5cf6, #a855f7); color: white; padding: 3px 10px; border-radius: 12px; font-size: 10px; margin-left: 10px; text-transform: uppercase; }' +
      '.declaration { background: ' + theme.primary + '05; border: 1px solid ' + theme.primary + '15; border-radius: 12px; padding: 30px; margin-top: 40px; }' +
      '.declaration h2 { border-left: none; padding-left: 0; }' +
      '.signature-block { margin-top: 30px; }' +
      '.signature-line { border-top: 2px solid ' + theme.primary + '30; width: 200px; margin-top: 60px; padding-top: 8px; font-size: 12px; color: ' + theme.text + '80; }' +
      '.footer { text-align: center; color: ' + theme.text + '60; font-size: 11px; margin-top: 50px; padding-top: 20px; border-top: 1px solid ' + theme.primary + '15; }' +
      '</style></head><body>' +
      '<div class="cover">' +
      logoHtml +
      '<h1>' + (reportName || 'ESG Report') + '</h1>' +
      '<div class="org">' + (currentOrg?.name || 'Organisation') + '</div>' +
      abnHtml +
      '<div class="period">Financial Year ' + (financialYear - 1) + '/' + String(financialYear).slice(-2) + '</div>' +
      '<div class="period">Report Date: ' + new Date(reportDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) + '</div>' +
      standardBadge +
      '</div>' +
      '<div class="toc"><h2>Table of Contents</h2><ul>' + tocItems + '</ul></div>' +
      sectionsHtml +
      declarationHtml +
      '<div class="footer"><p>This is a preview. The final PDF report may have minor styling variations.</p>' + platformLogoHtml + '</div>' +
      '</body></html>';

    setPreviewHtml(html);
    setShowPreview(true);
  };
  // Check if section has required data
  const checkSectionDataRequirements = (section) => {
    if (!section.data_requirements) return { allMet: true, missing: [] };

    const requirements = typeof section.data_requirements === 'string'
      ? JSON.parse(section.data_requirements)
      : section.data_requirements;

    const missing = requirements.filter(req => !dataCompleteness[req]?.met);

    return {
      allMet: missing.length === 0,
      missing,
    };
  };

  // Get sections grouped by category
  const getSectionsByCategory = () => {
    const grouped = {};
    sections.forEach(section => {
      if (!grouped[section.category]) {
        grouped[section.category] = [];
      }
      grouped[section.category].push(section);
    });
    return grouped;
  };

  // Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedTemplate !== null;
      case 2: return Object.values(selectedSections).some(s => s.included);
      case 3: return true; // Content is optional
      case 4: return selectedTheme !== null;
      case 5: return true;
      default: return true;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#003B5C] mx-auto mb-4" />
          <p className="text-slate-600">Loading Report Builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reports')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div>
                <h1 className="text-lg font-semibold text-[#003B5C]">Report Builder</h1>
                {selectedTemplate && currentStep > 1 && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {selectedTemplate.name}
                    {selectedTemplate.primary_standard && (
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] ml-1">
                        {selectedTemplate.primary_standard}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              {currentOrg && (
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {currentOrg.name}
                </span>
              )}
              <div className="h-6 w-px bg-slate-200" />

              {/* Save/Load Controls */}
              <div className="flex items-center gap-2">
                {savedDrafts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDraftSelector(true)}
                    className="text-slate-600"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Load Draft
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => saveDraft()}
                  disabled={saving || !selectedTemplate}
                  className="text-slate-600"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  Save Progress
                </Button>
                {lastSavedAt && (
                  <span className="text-xs text-slate-400">
                    Saved {lastSavedAt.toLocaleTimeString()}
                  </span>
                )}
              </div>

              <div className="h-6 w-px bg-slate-200" />
              <span className="text-sm text-slate-500">
                {calculateCompleteness()}% Complete
              </span>
              <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#74A57F] to-[#003B5C] transition-all duration-500"
                  style={{ width: `${calculateCompleteness()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {WIZARD_STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => isCompleted && setCurrentStep(step.id)}
                    disabled={!isCompleted && !isActive}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${isActive
                        ? 'bg-[#003B5C] text-white'
                        : isCompleted
                          ? 'bg-[#74A57F]/10 text-[#74A57F] hover:bg-[#74A57F]/20 cursor-pointer'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive
                        ? 'bg-white/20'
                        : isCompleted
                          ? 'bg-[#74A57F]/20'
                          : 'bg-slate-200'
                      }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium">{step.name}</div>
                      <div className={`text-xs ${isActive ? 'text-white/70' : 'text-slate-500'}`}>
                        {step.description}
                      </div>
                    </div>
                  </button>

                  {idx < WIZARD_STEPS.length - 1 && (
                    <ChevronRight className={`w-5 h-5 mx-2 ${currentStep > step.id ? 'text-[#74A57F]' : 'text-slate-300'
                      }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Step 1: Template Selection */}
        {currentStep === 1 && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#003B5C]">Configure Your Report</h2>
              <p className="text-slate-600 mt-2">
                Select your reporting period and choose a report template.
              </p>
            </div>

            {/* Reporting Period Selection */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-[#003B5C] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Reporting Period
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <button
                  onClick={() => setReportingPeriod('financial_year')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${reportingPeriod === 'financial_year'
                      ? 'border-[#003B5C] bg-[#003B5C]/5'
                      : 'border-slate-200 hover:border-[#74A57F]'
                    }`}
                >
                  <div className="font-medium text-slate-800">Financial Year</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Report for a specific Australian FY (July 1 - June 30)
                  </div>
                </button>

                <button
                  onClick={() => setReportingPeriod('all_time')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${reportingPeriod === 'all_time'
                      ? 'border-[#003B5C] bg-[#003B5C]/5'
                      : 'border-slate-200 hover:border-[#74A57F]'
                    }`}
                >
                  <div className="font-medium text-slate-800">All Time</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Include all emissions data ever recorded
                  </div>
                </button>

                <button
                  onClick={() => setReportingPeriod('custom')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${reportingPeriod === 'custom'
                      ? 'border-[#003B5C] bg-[#003B5C]/5'
                      : 'border-slate-200 hover:border-[#74A57F]'
                    }`}
                >
                  <div className="font-medium text-slate-800">Custom Range</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Specify a custom date range
                  </div>
                </button>
              </div>

              {/* Financial Year Selector */}
              {reportingPeriod === 'financial_year' && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-sm text-slate-600 mb-2 block">Select Financial Year</Label>
                  <Select
                    value={String(financialYear)}
                    onValueChange={(v) => setFinancialYear(parseInt(v))}
                  >
                    <SelectTrigger className="w-full md:w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(year => (
                        <SelectItem key={year} value={String(year)}>
                          FY{year - 1}/{String(year).slice(-2)} (Jul {year - 1} - Jun {year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Assurance Status Badge */}
                  {currentOrg?.asrs_reporting_group && (
                    <div className="mt-4 flex items-center gap-3">
                      <AssuranceBadge asrsGroup={currentOrg.asrs_reporting_group} financialYear={financialYear} />
                      <span className="text-xs text-slate-500">
                        Based on ASRS {ASSURANCE_REQUIREMENTS[currentOrg.asrs_reporting_group]?.name}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* All Time Info */}
              {reportingPeriod === 'all_time' && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Info className="w-4 h-4 text-blue-500" />
                    <span>This will include all emissions data from your first entry to today.</span>
                  </div>
                </div>
              )}

              {/* Custom Range */}
              {reportingPeriod === 'custom' && (
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600 mb-2 block">Start Date</Label>
                    <Input type="date" className="w-full" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 mb-2 block">End Date</Label>
                    <Input type="date" className="w-full" />
                  </div>
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div>
              <h3 className="font-semibold text-[#003B5C] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Choose Report Template
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => {
                  const isSelected = selectedTemplate?.id === template.id;

                  return (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-6 rounded-xl border-2 text-left transition-all hover:shadow-lg ${isSelected
                          ? 'border-[#003B5C] bg-[#003B5C]/5 shadow-md'
                          : 'border-slate-200 bg-white hover:border-[#74A57F]'
                        }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isSelected ? 'bg-[#003B5C] text-white' : 'bg-slate-100 text-slate-600'
                          }`}>
                          <FileText className="w-6 h-6" />
                        </div>
                        {template.is_premium && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Premium
                          </span>
                        )}
                      </div>

                      <h3 className="font-semibold text-lg text-[#003B5C] mb-2">{template.name}</h3>
                      <p className="text-sm text-slate-600 mb-4">{template.description}</p>

                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          ~{template.estimated_time_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          {[...Array(template.complexity_level || 1)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                          ))}
                        </span>
                      </div>

                      {template.primary_standard && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {template.primary_standard}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Custom Templates Section */}
              {customTemplates.length > 0 && (
                <div className="mt-8">
                  <h4 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Your Custom Templates
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {customTemplates.map((customTpl) => {
                      const isSelected = selectedTemplate?.id === customTpl.id;

                      return (
                        <div
                          key={customTpl.id}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${isSelected
                              ? 'border-[#003B5C] bg-[#003B5C]/5'
                              : 'border-slate-200 bg-white hover:border-[#74A57F]'
                            }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <button
                              onClick={() => handleCustomTemplateSelect(customTpl)}
                              className="flex-1 text-left"
                            >
                              <h4 className="font-semibold text-[#003B5C]">{customTpl.name}</h4>
                              {customTpl.description && (
                                <p className="text-xs text-slate-500 mt-1">{customTpl.description}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-2">
                                {customTpl.section_config?.length || 0} sections
                              </p>
                            </button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Delete this custom template?')) {
                                  deleteCustomTemplate(customTpl.id);
                                }
                              }}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 -mr-2"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            Custom
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Create Custom Template Button */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setShowCustomTemplateBuilder(true)}
                  className="text-slate-600"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Create Custom Template
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Select a base template and customize sections to create your own reusable template.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Section Selection */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#003B5C]">Select Report Sections</h2>
              <p className="text-slate-600 mt-2">
                Choose which sections to include in your report. Required sections are pre-selected.
              </p>
            </div>

            {Object.entries(getSectionsByCategory()).map(([category, categorySections]) => {
              const CategoryIcon = CATEGORY_ICONS[category] || FileText;
              const relevantSections = categorySections.filter(s =>
                selectedSections[s.slug] !== undefined ||
                !selectedTemplate?.sections?.length
              );

              if (relevantSections.length === 0) return null;

              return (
                <div key={category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                    <CategoryIcon className="w-5 h-5 text-[#003B5C]" />
                    <h3 className="font-semibold text-[#003B5C]">{CATEGORY_LABELS[category]}</h3>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {relevantSections.map((section) => {
                      const isIncluded = selectedSections[section.slug]?.included;
                      const isRequired = selectedSections[section.slug]?.required;
                      const dataReq = checkSectionDataRequirements(section);

                      return (
                        <div
                          key={section.slug}
                          className={`px-6 py-4 flex items-center justify-between ${isIncluded ? 'bg-white' : 'bg-slate-50/50'
                            }`}
                        >
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => toggleSection(section.slug, isRequired)}
                              disabled={isRequired}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${isIncluded
                                  ? 'bg-[#003B5C] border-[#003B5C] text-white'
                                  : 'border-slate-300 hover:border-[#74A57F]'
                                } ${isRequired ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              {isIncluded && <Check className="w-4 h-4" />}
                            </button>

                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{section.display_name}</span>
                                {isRequired && (
                                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                                    Required
                                  </span>
                                )}
                                {section.supports_ai_generation && (
                                  <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" />
                                    AI
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {(() => {
                              // Determine data status based on section requirements
                              const requirements = section.data_requirements
                                ? (typeof section.data_requirements === 'string'
                                  ? JSON.parse(section.data_requirements)
                                  : section.data_requirements)
                                : [];

                              if (requirements.length === 0) {
                                return (
                                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded flex items-center gap-1">
                                    <Info className="w-3 h-3" />
                                    No data required
                                  </span>
                                );
                              }

                              // Check how many requirements are met
                              const metCount = requirements.filter(req => dataCompleteness[req]?.met).length;
                              const totalCount = requirements.length;

                              if (metCount === totalCount) {
                                return (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Data available ({dataCompleteness[requirements[0]]?.count || 0} entries)
                                  </span>
                                );
                              } else if (metCount > 0) {
                                return (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Partial data ({metCount}/{totalCount})
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded flex items-center gap-1">
                                    <X className="w-3 h-3" />
                                    No data entered
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 3: Content Generation */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#003B5C]">Review Data & Generate Content</h2>
              <p className="text-slate-600 mt-2">
                Review your existing data and generate professional narratives for each section.
              </p>
            </div>

            <div className="space-y-4">
              {Object.entries(selectedSections)
                .filter(([_, v]) => v.included)
                .map(([slug]) => {
                  const section = sections.find(s => s.slug === slug);
                  if (!section) return null;

                  const content = sectionContent[slug];
                  const isEditing = editingSection === slug;
                  const relatedWording = sampleWording.filter(w => w.section_slug === slug);

                  // Check what data exists for this section
                  const isMetricsSection = section.category === 'metrics';
                  const isGovernanceSection = section.category === 'governance';
                  const isRiskSection = section.category === 'risk';
                  const isSocialSection = section.category === 'social';
                  const isMaterialitySection = slug.includes('materiality');
                  const isScenarioSection = slug.includes('scenario');

                  const hasEmissionsData = orgData.emissions?.length > 0;
                  const hasOffsetsData = orgData.offsets?.length > 0;
                  const hasGovernanceData = orgData.governance?.length > 0;
                  const hasRiskData = orgData.risks?.length > 0;
                  const hasSocialData = orgData.social?.length > 0;
                  const hasMaterialityData = orgData.materiality?.length > 0;
                  const hasScenarioData = orgData.scenarios?.length > 0;

                  // Helper to get emission value (uses total_tco2e or co2_equivalent)
                  const getVal = (e) => e.total_tco2e || e.co2_equivalent || 0;

                  // Calculate totals for display
                  const scope1Total = orgData.emissions?.filter(e =>
                    ['fuel_petrol', 'fuel_diesel', 'fuel_lpg', 'natural_gas'].includes(e.category)
                  ).reduce((sum, e) => sum + getVal(e), 0) || 0;

                  const scope2Total = orgData.emissions?.filter(e =>
                    ['electricity', 'electricity_greenpower'].includes(e.category)
                  ).reduce((sum, e) => sum + getVal(e), 0) || 0;

                  // Note: Scope 3 has been removed from this platform
                  const totalEmissions = scope1Total + scope2Total;

                  return (
                    <div key={slug} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-[#003B5C]">{section.display_name}</h3>
                          {content?.isAiGenerated && (
                            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              AI Generated
                            </span>
                          )}
                          {content?.wasEdited && (
                            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded flex items-center gap-1">
                              <Edit3 className="w-3 h-3" />
                              Edited
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {/* AI Refine button for sample text */}
                          {content?.isSampleText && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRefineSampleText(section)}
                              disabled={refiningSection === section.slug}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              {refiningSection === section.slug ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Wand2 className="w-4 h-4 mr-2" />
                              )}
                              {refiningSection === section.slug ? 'Refining...' : 'Refine with AI'}
                            </Button>
                          )}

                          {section.supports_ai_generation && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateWithLoading(section)}
                              disabled={isGenerating || generatingSection === section.slug}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            >
                              {(isGenerating || generatingSection === section.slug) ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              {generatingSection === section.slug ? 'Generating...' : 'Generate Narrative'}
                            </Button>
                          )}

                          {relatedWording.length > 0 && (
                            <Select
                              onValueChange={(value) => {
                                const wording = relatedWording.find(w => String(w.id) === String(value));
                                if (wording) {
                                  handleUseSampleWording(section, wording);
                                }
                              }}
                              value={content?.sampleWordingId || undefined}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Sample text..." />
                              </SelectTrigger>
                              <SelectContent>
                                {relatedWording.map(w => (
                                  <SelectItem key={w.id} value={String(w.id)}>
                                    {w.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>

                      <div className="p-6">
                        {/* Show existing data summary for metrics sections */}
                        {isMetricsSection && hasEmissionsData && (
                          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Check className="w-4 h-4 text-emerald-600" />
                              <span className="font-medium text-emerald-800">Your Emissions Data</span>
                              <span className="text-xs text-emerald-600">({orgData.emissions?.length} entries)</span>
                            </div>

                            {slug.includes('ghg') || slug.includes('scope') ? (
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div className="bg-white p-3 rounded-lg">
                                  <div className="text-slate-500 text-xs">Scope 1</div>
                                  <div className="font-semibold text-lg text-[#E76F51]">
                                    {scope1Total.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-slate-400">tCO₂-e</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <div className="text-slate-500 text-xs">Scope 2</div>
                                  <div className="font-semibold text-lg text-[#F4A261]">
                                    {scope2Total.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-slate-400">tCO₂-e</div>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                  <div className="text-slate-500 text-xs">Total</div>
                                  <div className="font-semibold text-lg text-[#003B5C]">
                                    {totalEmissions.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-slate-400">tCO₂-e</div>
                                </div>
                              </div>
                            ) : slug.includes('category') ? (
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                {Object.entries(
                                  orgData.emissions?.reduce((acc, e) => {
                                    acc[e.category] = (acc[e.category] || 0) + getVal(e);
                                    return acc;
                                  }, {}) || {}
                                ).slice(0, 6).map(([cat, val]) => (
                                  <div key={cat} className="bg-white p-2 rounded flex justify-between items-center">
                                    <span className="text-slate-600 capitalize">{cat.replace(/_/g, ' ')}</span>
                                    <span className="font-medium">{val.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : slug.includes('offset') && hasOffsetsData ? (
                              <div className="text-sm text-emerald-700">
                                {orgData.offsets?.length} carbon offset records available
                              </div>
                            ) : (
                              <div className="text-sm text-emerald-700">
                                Total emissions: <strong>{totalEmissions.toFixed(2)} tCO₂-e</strong>
                              </div>
                            )}

                            <p className="text-xs text-emerald-600 mt-3">
                              This data will be included in your report. Use "Generate Narrative" to create professional text.
                            </p>
                          </div>
                        )}

                        {/* Show message for sections without data */}
                        {isMetricsSection && !hasEmissionsData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No emissions data found</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Add emissions entries on the Dashboard or Entries page to populate this section.
                            </p>
                          </div>
                        )}

                        {/* Governance Data Display */}
                        {isGovernanceSection && hasGovernanceData && (
                          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Building2 className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-blue-800">Your Governance Data</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-blue-600 uppercase tracking-wide">Items Recorded</div>
                                <div className="font-semibold text-blue-900">{orgData.governance?.length || 0}</div>
                              </div>
                              {orgData.governance?.filter(g => g.item_type === 'board_member').length > 0 && (
                                <div className="bg-white/60 p-3 rounded">
                                  <div className="text-xs text-blue-600 uppercase tracking-wide">Board Members</div>
                                  <div className="font-semibold text-blue-900">
                                    {orgData.governance?.filter(g => g.item_type === 'board_member').length}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {isGovernanceSection && !hasGovernanceData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No governance data found</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Add governance details on the Governance page to populate this section.
                            </p>
                          </div>
                        )}

                        {/* Risk Data Display */}
                        {isRiskSection && hasRiskData && (
                          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Shield className="w-4 h-4 text-rose-600" />
                              <span className="font-medium text-rose-800">Your Climate Risks</span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-rose-600 uppercase tracking-wide">Total Risks</div>
                                <div className="font-semibold text-rose-900">{orgData.risks?.length || 0}</div>
                              </div>
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-rose-600 uppercase tracking-wide">Physical</div>
                                <div className="font-semibold text-rose-900">
                                  {orgData.risks?.filter(r => r.risk_type === 'physical').length || 0}
                                </div>
                              </div>
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-rose-600 uppercase tracking-wide">Transition</div>
                                <div className="font-semibold text-rose-900">
                                  {orgData.risks?.filter(r => r.risk_type === 'transition').length || 0}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isRiskSection && !hasRiskData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No climate risks identified</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Add climate risks on the Risks page to populate this section.
                            </p>
                          </div>
                        )}

                        {/* Social Metrics Data Display */}
                        {isSocialSection && hasSocialData && (
                          <div className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Users className="w-4 h-4 text-violet-600" />
                              <span className="font-medium text-violet-800">Your Social Metrics</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-violet-600 uppercase tracking-wide">Records</div>
                                <div className="font-semibold text-violet-900">{orgData.social?.length || 0}</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isSocialSection && !hasSocialData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No social metrics found</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Add social metrics data on the Social page to populate this section.
                            </p>
                          </div>
                        )}

                        {/* Materiality Data Display */}
                        {isMaterialitySection && hasMaterialityData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Scale className="w-4 h-4 text-amber-600" />
                              <span className="font-medium text-amber-800">Your Materiality Assessment</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-amber-600 uppercase tracking-wide">Topics Assessed</div>
                                <div className="font-semibold text-amber-900">{orgData.materiality?.length || 0}</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isMaterialitySection && !hasMaterialityData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No materiality assessment found</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Complete a materiality assessment on the Materiality page to populate this section.
                            </p>
                          </div>
                        )}

                        {/* Scenario Analysis Data Display */}
                        {isScenarioSection && hasScenarioData && (
                          <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <TrendingUp className="w-4 h-4 text-cyan-600" />
                              <span className="font-medium text-cyan-800">Your Scenario Analyses</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="bg-white/60 p-3 rounded">
                                <div className="text-xs text-cyan-600 uppercase tracking-wide">Analyses Saved</div>
                                <div className="font-semibold text-cyan-900">{orgData.scenarios?.length || 0}</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isScenarioSection && !hasScenarioData && (
                          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">No scenario analysis found</span>
                            </div>
                            <p className="text-sm text-amber-600 mt-2">
                              Run a scenario stress test to populate this section with climate scenario data.
                            </p>
                          </div>
                        )}

                        {/* Narrative content area */}
                        {isEditing ? (
                          <div className="space-y-4">
                            <Label>Narrative Content</Label>
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="min-h-[200px]"
                              placeholder="Enter your narrative content here..."
                            />
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingSection(null);
                                  setEditContent('');
                                }}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSaveEdit(slug)}
                                className="bg-[#003B5C]"
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : content?.content ? (
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium text-slate-600">Narrative</span>
                            </div>
                            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                              {content.content}
                            </div>
                            <div className="flex items-center gap-2 justify-end pt-4 border-t border-slate-100">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingSection(slug);
                                  setEditContent(content.content);
                                }}
                              >
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                            </div>

                            {content.aiAuditId && (
                              <div className="text-xs text-slate-400 flex items-center gap-2 pt-2">
                                <ShieldCheck className="w-3 h-3" />
                                Audit ID: {content.aiAuditId.slice(0, 8)}...
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-slate-400 border-t border-slate-100">
                            <p className="text-sm mb-3">
                              {section.supports_ai_generation
                                ? 'Click "Generate Narrative" to create professional text for this section.'
                                : 'Add your own content for this section.'}
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingSection(slug);
                                setEditContent('');
                              }}
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Write Manually
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Disclaimer for Step 3 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mt-6">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">AI-Generated Content Notice</p>
                <p className="mt-1">
                  The "Generate with AI" feature uses artificial intelligence to draft narrative content based on your data.
                  <strong> You must review and verify all AI-generated text before publication.</strong> AI processing is ephemeral
                  and no organisational data is permanently stored by the AI service.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Branding */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#003B5C]">Customize Branding</h2>
              <p className="text-slate-600 mt-2">
                Choose a color theme and add your organization details for the report.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Theme Selection */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-[#003B5C] mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Color Theme
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  {themes.map((theme) => {
                    const isSelected = selectedTheme?.id === theme.id;

                    return (
                      <button
                        key={theme.id}
                        onClick={() => setSelectedTheme(theme)}
                        className={`p-4 rounded-lg border-2 flex items-center gap-4 transition-all ${isSelected
                            ? 'border-[#003B5C] bg-[#003B5C]/5'
                            : 'border-slate-200 hover:border-[#74A57F]'
                          }`}
                      >
                        <div className="flex gap-1">
                          <div
                            className="w-6 h-6 rounded-full border border-white shadow-sm"
                            style={{ backgroundColor: theme.primary_color }}
                          />
                          <div
                            className="w-6 h-6 rounded-full border border-white shadow-sm -ml-2"
                            style={{ backgroundColor: theme.secondary_color }}
                          />
                          <div
                            className="w-6 h-6 rounded-full border border-white shadow-sm -ml-2"
                            style={{ backgroundColor: theme.accent_color }}
                          />
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-slate-800">{theme.name}</div>
                          <div className="text-xs text-slate-500">{theme.description}</div>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-[#003B5C] ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Report Details */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-[#003B5C] mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Report Details
                </h3>

                <div className="space-y-4">
                  <div>
                    <Label>Report Name</Label>
                    <Input
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                      placeholder="e.g., Annual Sustainability Report 2025"
                    />
                  </div>

                  <div>
                    <Label>Report Date</Label>
                    <Input
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Date shown on the report (defaults to today)
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <Label>Authorised Officer (for Declaration)</Label>
                    <div className="mt-2 space-y-3">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="officerSource"
                            checked={authorisedOfficerSource === 'governance'}
                            onChange={() => setAuthorisedOfficerSource('governance')}
                            className="text-[#003B5C]"
                          />
                          <span className="text-sm">Select from Governance</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="officerSource"
                            checked={authorisedOfficerSource === 'manual'}
                            onChange={() => setAuthorisedOfficerSource('manual')}
                            className="text-[#003B5C]"
                          />
                          <span className="text-sm">Enter manually</span>
                        </label>
                      </div>

                      {authorisedOfficerSource === 'governance' ? (
                        <Select
                          value={selectedOfficerId}
                          onValueChange={(v) => {
                            setSelectedOfficerId(v);
                            const officer = governancePersonnel.find(p => p.id === v);
                            if (officer) {
                              setDirectorsName(officer.full_name);
                              setDirectorsTitle(officer.title);
                              // Load signature if available
                              if (officer.signature_image) {
                                setDirectorsSignature(officer.signature_image);
                              }
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select authorised officer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {governancePersonnel.map(person => (
                              <SelectItem key={person.id} value={person.id}>
                                <div className="flex items-center gap-2">
                                  <span>{person.full_name}</span>
                                  <span className="text-slate-400">-</span>
                                  <span className="text-slate-500 text-sm">{person.title}</span>
                                  {person.role_type === 'responsible_officer' && (
                                    <span className="text-xs bg-[#74A57F]/20 text-[#74A57F] px-1.5 py-0.5 rounded">RO</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <>
                          <Input
                            value={directorsName}
                            onChange={(e) => setDirectorsName(e.target.value)}
                            placeholder="Full name"
                          />
                          <Input
                            value={directorsTitle}
                            onChange={(e) => setDirectorsTitle(e.target.value)}
                            placeholder="Title (e.g., Managing Director)"
                          />
                        </>
                      )}

                      {governancePersonnel.length === 0 && authorisedOfficerSource === 'governance' && (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          No personnel found. Add personnel in the Governance module or enter details manually.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <Label>Electronic Signature (Optional)</Label>
                    <div className="mt-2">
                      {directorsSignature ? (
                        <div className="space-y-3">
                          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                            <img
                              src={directorsSignature}
                              alt="Director's signature"
                              className="max-h-20 mx-auto"
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDirectorsSignature(null)}
                            className="w-full"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Remove Signature
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#74A57F] hover:bg-slate-50 transition-colors">
                            <div className="flex flex-col items-center justify-center py-4">
                              <Upload className="w-6 h-6 text-slate-400 mb-1" />
                              <p className="text-sm text-slate-500">Upload signature image</p>
                              <p className="text-xs text-slate-400">PNG or JPG (transparent background recommended)</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/png,image/jpeg"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setDirectorsSignature(reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                          <p className="text-xs text-slate-500">
                            The signature will appear in the Director's Declaration section of the report.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer for Step 4 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important Compliance Notice</p>
                <p className="mt-1">
                  The authorised officer signing this report takes responsibility for the accuracy of its contents.
                  Providing false or misleading sustainability information may constitute greenwashing, which is subject to
                  regulatory penalties under Australian Consumer Law and ASIC regulations.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Review & Generate */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#003B5C]">Review & Generate</h2>
              <p className="text-slate-600 mt-2">
                Review your report configuration and generate the final PDF.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Summary */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#003B5C] mb-4">Report Summary</h3>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Template:</span>
                      <p className="font-medium">{selectedTemplate?.name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Reporting Period:</span>
                      <p className="font-medium">
                        {reportingPeriod === 'financial_year'
                          ? `FY${financialYear - 1}/${String(financialYear).slice(-2)}`
                          : reportingPeriod === 'all_time'
                            ? 'All Time'
                            : 'Custom Range'}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500">Report Date:</span>
                      <p className="font-medium">{new Date(reportDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Sections:</span>
                      <p className="font-medium">
                        {Object.values(selectedSections).filter(s => s.included).length} selected
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500">Theme:</span>
                      <p className="font-medium">{selectedTheme?.name}</p>
                    </div>
                    {directorsName && (
                      <div>
                        <span className="text-slate-500">Director:</span>
                        <p className="font-medium">{directorsName}{directorsTitle ? `, ${directorsTitle}` : ''}</p>
                      </div>
                    )}
                  </div>

                  {directorsSignature && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <span className="text-slate-500 text-sm">Signature:</span>
                      <div className="mt-2 p-2 bg-slate-50 rounded inline-block">
                        <img src={directorsSignature} alt="Signature" className="max-h-12" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Status */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#003B5C] mb-4">Content Status</h3>

                  <div className="space-y-3">
                    {Object.entries(selectedSections)
                      .filter(([_, v]) => v.included)
                      .map(([slug]) => {
                        const section = sections.find(s => s.slug === slug);
                        const content = sectionContent[slug];
                        const hasContent = content?.content?.length > 0;

                        return (
                          <div key={slug} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm">{section?.display_name}</span>
                            <div className="flex items-center gap-2">
                              {hasContent ? (
                                <>
                                  <Check className="w-4 h-4 text-emerald-500" />
                                  <span className="text-xs text-emerald-600">Ready</span>
                                  {content.isAiGenerated && (
                                    <Sparkles className="w-3 h-3 text-purple-500" />
                                  )}
                                </>
                              ) : (
                                <>
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                  <span className="text-xs text-amber-600">No content</span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              {/* Generate Panel */}
              <div className="bg-gradient-to-br from-[#003B5C] to-[#005a8c] rounded-xl p-6 text-white">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold">{reportName}</h3>
                  <p className="text-white/70 text-sm mt-1">
                    {selectedTemplate?.primary_standard || 'Custom Report'}
                  </p>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-white/70">Completeness</span>
                    <span className="font-medium">{calculateCompleteness()}%</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/10">
                    <span className="text-white/70">AI Sections</span>
                    <span className="font-medium">
                      {Object.values(sectionContent).filter(c => c.isAiGenerated).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-white/70">Estimated Pages</span>
                    <span className="font-medium">
                      ~{Math.max(5, Object.values(selectedSections).filter(s => s.included).length * 2)}
                    </span>
                  </div>
                </div>

                {/* Mandatory Confirmation Checkbox */}
                <div className="bg-white/10 rounded-lg p-4 mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reviewConfirmed}
                      onChange={(e) => setReviewConfirmed(e.target.checked)}
                      className="mt-1 rounded border-white/30"
                    />
                    <span className="text-sm text-white/90">
                      I confirm that I have reviewed all content in this report, including any AI-generated text,
                      and take responsibility for its accuracy. I understand that providing false or misleading
                      sustainability information may constitute greenwashing and is subject to regulatory penalties.
                    </span>
                  </label>
                </div>

                {/* Output Format Selection */}
                <div className="bg-white/10 rounded-lg p-4 mb-4">
                  <label className="block text-sm text-white/80 mb-2">Output Format</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setOutputFormat('pdf')}
                      className={`flex-1 py-2 px-4 rounded-lg transition text-sm font-medium ${outputFormat === 'pdf'
                          ? 'bg-white text-[#003B5C]'
                          : 'bg-white/20 text-white hover:bg-white/30'
                        }`}
                    >
                      PDF Document
                    </button>
                    <button
                      onClick={() => setOutputFormat('docx')}
                      className={`flex-1 py-2 px-4 rounded-lg transition text-sm font-medium ${outputFormat === 'docx'
                          ? 'bg-white text-[#003B5C]'
                          : 'bg-white/20 text-white hover:bg-white/30'
                        }`}
                    >
                      Word Document
                    </button>
                  </div>
                </div>

                <Button
                  onClick={generatePreview}
                  variant="outline"
                  className="w-full mb-3 bg-transparent border-white/30 text-white hover:bg-white/10"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Report
                </Button>

                <Button
                  onClick={handleGenerateReport}
                  disabled={generating || !reviewConfirmed}
                  className="w-full bg-white text-[#003B5C] hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Generate {outputFormat === 'docx' ? 'Word' : 'PDF'} Report
                    </>
                  )}
                </Button>

                {!reviewConfirmed && (
                  <p className="text-xs text-white/70 text-center mt-2">
                    Please confirm you have reviewed the content to enable report generation
                  </p>
                )}

                <p className="text-xs text-white/50 text-center mt-4">
                  You'll receive an email when your report is ready
                </p>
              </div>
            </div>

            {/* Legal Disclaimer */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-6">
              <p className="text-xs text-slate-600">
                <strong>Disclaimer:</strong> Lucent Sky Pty Ltd provides this platform as a tool to assist with sustainability
                reporting. The accuracy and completeness of report content remains the sole responsibility of the user.
                Lucent Sky accepts no liability for any errors, omissions, or misrepresentations in reports generated using this platform.
                Users should seek independent professional advice where appropriate.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep < 5 ? (
            <Button
              onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
              disabled={!canProceed()}
              className="bg-[#003B5C]"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleGenerateReport}
              disabled={generating || !reviewConfirmed}
              className="bg-gradient-to-r from-[#74A57F] to-[#003B5C] disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* AI Co-Pilot Chat (Pro users only, Report Builder context) */}
      <AICoPilot
        organisationId={currentOrg?.id}
        organisationName={currentOrg?.name}
        currentSection={WIZARD_STEPS[currentStep - 1]?.name}
        subscriptionTier={currentOrg?.subscription_tier || 'pro'} // Report Builder is Pro-only anyway
        currentPage="report_builder"
      />

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg text-[#003B5C]">Report Preview</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Note: Final PDF styling may vary slightly</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full min-h-[600px] border-0"
                title="Report Preview"
              />
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-slate-50">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Close
              </Button>
              <Button
                onClick={() => { setShowPreview(false); handleGenerateReport(); }}
                disabled={!reviewConfirmed}
                className="bg-[#003B5C]"
              >
                <Download className="w-4 h-4 mr-2" />
                Generate PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Draft Selector Modal */}
      {showDraftSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg text-[#003B5C]">Load Saved Draft</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDraftSelector(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 max-h-96 overflow-auto">
              {savedDrafts.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No saved drafts found</p>
              ) : (
                <div className="space-y-2">
                  {savedDrafts.map(draft => (
                    <div
                      key={draft.id}
                      className={`p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition ${currentDraftId === draft.id ? 'border-[#003B5C] bg-blue-50' : 'border-slate-200'
                        }`}
                      onClick={() => loadDraft(draft.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-[#003B5C]">{draft.name}</h4>
                          <p className="text-sm text-slate-500">
                            FY{draft.financial_year} • {draft.template_slug?.replace(/-/g, ' ')}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Last saved: {new Date(draft.updated_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {currentDraftId === draft.id && (
                            <span className="text-xs bg-[#003B5C] text-white px-2 py-0.5 rounded">
                              Current
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm('Delete this draft?')) {
                                deleteDraft(draft.id);
                              }
                            }}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-4 border-t bg-slate-50">
              <Button
                variant="outline"
                onClick={startNewReport}
                className="text-slate-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Start New Report
              </Button>
              <Button variant="outline" onClick={() => setShowDraftSelector(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Template Builder Modal */}
      {showCustomTemplateBuilder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg text-[#003B5C]">Create Custom Template</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCustomTemplateBuilder(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              <p className="text-sm text-slate-600 mb-4">
                First, select a base template and customize the sections in Step 2. Then come back here to save your custom template.
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Template Name *</Label>
                  <Input
                    value={customTemplateName}
                    onChange={(e) => setCustomTemplateName(e.target.value)}
                    placeholder="e.g., Annual ESG Report"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Description (optional)</Label>
                  <Textarea
                    value={customTemplateDescription}
                    onChange={(e) => setCustomTemplateDescription(e.target.value)}
                    placeholder="Brief description of this template..."
                    className="mt-1"
                    rows={2}
                  />
                </div>

                {selectedTemplate && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Base Template:</strong> {selectedTemplate.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      <strong>Selected Sections:</strong> {Object.values(selectedSections).filter(s => s.included).length} sections
                    </p>
                  </div>
                )}

                {!selectedTemplate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                    Please select a base template first (Step 1), then customize sections (Step 2) before saving.
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-slate-50">
              <Button variant="outline" onClick={() => setShowCustomTemplateBuilder(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveAsCustomTemplate}
                disabled={!selectedTemplate || !customTemplateName.trim() || savingCustomTemplate}
                className="bg-[#003B5C]"
              >
                {savingCustomTemplate ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
